import React, { useState } from "react";
import { motion } from "framer-motion";
import { Mail, Phone, MessageCircle, Send, Camera, Video, Sparkles } from "lucide-react";
import { toast, Toaster } from "sonner";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import useTitle from "../lib/useTitle";
import { CONTACT } from "../lib/constants";

const heroImg = "https://images.unsplash.com/photo-1727425836905-0c2566febca3?w=2000&q=80";

const initialForm = { full_name: "", email: "", instagram: "", tiktok: "", youtube: "", audience_size: "", preferred_dates: "", message: "" };

const perks = (t) => [
  { icon: Camera, label: t.creator.perks.stay },
  { icon: Video, label: t.creator.perks.tour },
  { icon: Sparkles, label: t.creator.perks.guide },
];

export default function Creator() {
  const { t } = useI18n();
  useTitle(t.nav.creator);
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email) return toast.error("Name & email required");
    setSubmitting(true);
    try {
      await api.post("/creator-applications", form);
      toast.success(t.creator.thanks);
      setForm(initialForm);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally { setSubmitting(false); }
  };

  const inputCls = "w-full bg-white/80 border border-sand-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-wostel-sun focus:ring-2 focus:ring-wostel-sun/20 transition";

  return (
    <main data-testid="creator-page">
      <Toaster position="top-center" richColors />

      {/* HERO */}
      <section className="relative min-h-[55vh] flex items-end overflow-hidden bg-wostel-sun">
        <img src={heroImg} alt="" className="absolute inset-0 w-full h-full object-cover mix-blend-multiply opacity-90"/>
        <div className="absolute inset-0 bg-gradient-to-t from-wostel-sun via-wostel-sun/40 to-transparent"/>
        <div className="container-x relative z-10 pb-16 pt-32 text-wostel-ink">
          <span className="eyebrow">{t.nav.creator}</span>
          <h1 className="font-extrabold text-5xl sm:text-6xl lg:text-7xl tracking-tight mt-5 max-w-4xl leading-[0.95]">{t.creator.title}</h1>
          <p className="mt-6 max-w-2xl text-lg leading-relaxed">{t.creator.sub}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            {perks(t).map((p, i) => (
              <div key={i} className="bg-wostel-ink text-sand-50 px-4 py-2 rounded-full inline-flex items-center gap-2 text-sm font-semibold">
                <p.icon size={14}/> {p.label}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FORM */}
      <section className="container-x py-20">
        <div className="grid md:grid-cols-12 gap-12">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}
            className="md:col-span-7 bg-sand-100 rounded-3xl p-8 sm:p-10 border border-sand-300/60">
            <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="creator-form">
              <div className="sm:col-span-2">
                <label className="text-xs uppercase tracking-wider text-wostel-ink/60 mb-2 block font-semibold">{t.creator.form.full_name}*</label>
                <input required value={form.full_name} onChange={onChange("full_name")} className={inputCls} data-testid="creator-input-name" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-wostel-ink/60 mb-2 block font-semibold">{t.creator.form.email}*</label>
                <input required type="email" value={form.email} onChange={onChange("email")} className={inputCls} data-testid="creator-input-email" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-wostel-ink/60 mb-2 block font-semibold">{t.creator.form.audience_size}</label>
                <input value={form.audience_size} onChange={onChange("audience_size")} className={inputCls} data-testid="creator-input-audience" placeholder="50k" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-wostel-ink/60 mb-2 block font-semibold">{t.creator.form.instagram}</label>
                <input value={form.instagram} onChange={onChange("instagram")} className={inputCls} data-testid="creator-input-ig" placeholder="@" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-wostel-ink/60 mb-2 block font-semibold">{t.creator.form.tiktok}</label>
                <input value={form.tiktok} onChange={onChange("tiktok")} className={inputCls} data-testid="creator-input-tiktok" placeholder="@" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-wostel-ink/60 mb-2 block font-semibold">{t.creator.form.youtube}</label>
                <input value={form.youtube} onChange={onChange("youtube")} className={inputCls} data-testid="creator-input-yt" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-wostel-ink/60 mb-2 block font-semibold">{t.creator.form.preferred_dates}</label>
                <input value={form.preferred_dates} onChange={onChange("preferred_dates")} className={inputCls} data-testid="creator-input-dates" />
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs uppercase tracking-wider text-wostel-ink/60 mb-2 block font-semibold">{t.creator.form.message}</label>
                <textarea rows={5} value={form.message} onChange={onChange("message")} className={inputCls} data-testid="creator-input-message" />
              </div>
              <div className="sm:col-span-2 mt-2">
                <button type="submit" disabled={submitting} data-testid="creator-submit"
                  className="btn-pill bg-wostel-ink text-sand-50 hover:bg-wostel-cyan disabled:opacity-50 w-full sm:w-auto">
                  <Send size={16} /> {submitting ? "..." : t.creator.form.submit}
                </button>
              </div>
            </form>
          </motion.div>

          <aside className="md:col-span-5">
            <div className="sticky top-28 bg-wostel-navy text-sand-50 rounded-3xl p-10 overflow-hidden relative">
              <div className="absolute -top-12 -right-12 w-44 h-44 rounded-full bg-wostel-sun/20 blur-3xl"/>
              <h3 className="font-extrabold text-2xl mb-2 tracking-tight relative">{t.creator.contact_title}</h3>
              <p className="text-sm text-sand-50/70 mb-8 relative">Djerba · Tunisia</p>
              <ul className="space-y-5 relative">
                <li><a href={`mailto:${CONTACT.email}`} className="flex items-center gap-3 hover:text-wostel-sun" data-testid="creator-email"><Mail size={18}/> {CONTACT.email}</a></li>
                <li><a href={`tel:${CONTACT.phone.replace(/\s/g,'')}`} className="flex items-center gap-3 hover:text-wostel-sun" data-testid="creator-phone"><Phone size={18}/> {CONTACT.phone}</a></li>
                <li><a href={CONTACT.whatsapp} target="_blank" rel="noreferrer" className="flex items-center gap-3 hover:text-wostel-sun" data-testid="creator-whatsapp"><MessageCircle size={18}/> WhatsApp</a></li>
              </ul>
            </div>
          </aside>
        </div>
      </section>
    </main>
  );
}
